import { useMemo } from 'react'

import { Hex } from 'viem'

import { usePastStateChanges } from '~/components/txn-display/state-change/hooks/usePastStateChanges'
import { StateChangeItem } from '~/components/txn-display/state-change/types'
import { TransferType } from '~/types'
import { EvmAddress } from '~/utils/address'
import { isAutoEarnTransfer } from '~/utils/vault'

// Builds the state-change legs for an executed asset transfer, shared by the
// smart-account and card transfer dialogs. Watches the owned address plus the
// counterparty on outbound transfers; for inbound transfers it watches only the
// owned address and synthesizes the sender's outflow leg, so both legs show
// without surfacing the sender's unrelated events (e.g. a relay router's approvals).
export const useTransferStateChangeItems = ({
  ownedAddress,
  from,
  to,
  transferType,
  tokenAddress,
  transactionHash,
  chainId,
  transactionTime,
}: {
  ownedAddress: string
  from: string
  to: string
  transferType: (typeof TransferType)[keyof typeof TransferType]
  tokenAddress: string
  transactionHash: string
  chainId: number
  transactionTime: string
}) => {
  const isInbound = transferType === TransferType.Inbound

  const addressesToWatch = useMemo<EvmAddress.Type[]>(() => {
    const addresses = new Set<EvmAddress.Type>()
    const addAddr = (addr: string) => {
      if (EvmAddress.validate(addr)) {
        addresses.add(EvmAddress.format(addr))
      }
    }
    addAddr(ownedAddress)
    // Inbound `from` is an external sender (e.g. a relay router); its leg is
    // synthesized below rather than watched, so its unrelated events stay hidden.
    if (!isInbound) {
      addAddr(from)
    }
    // Auto-earn `to` is a vault contract, not the user's account.
    if (!isAutoEarnTransfer({ chainId, transferType, tokenAddress, to })) {
      addAddr(to)
    }
    return Array.from(addresses)
  }, [ownedAddress, from, to, tokenAddress, transferType, chainId, isInbound])

  const { stateChangeItems, isLoading } = usePastStateChanges({
    transactionHash: transactionHash as Hex,
    chainId,
    addressesToWatch,
    transactionTime,
  })

  // Mirror inbound inflows as a sender outflow so both legs of the transfer show.
  const displayItems = useMemo(() => {
    if (!isInbound) return stateChangeItems
    const transferItems = stateChangeItems.filter(
      (item) => item.state_change_type !== 'approval',
    )
    const fromAddr = EvmAddress.format(from)
    const senderItems: StateChangeItem[] = transferItems
      .filter(
        (
          item,
        ): item is Exclude<
          StateChangeItem,
          { state_change_type: 'approval' }
        > => 'direction' in item && item.direction === 'inflow',
      )
      .map((item) => ({
        ...item,
        id: `${item.id}-sender`,
        address: fromAddr,
        direction: 'outflow' as const,
        ...(item.state_change_type === 'erc-20'
          ? { amount: -item.amount }
          : {}),
      }))
    return [...senderItems, ...transferItems]
  }, [stateChangeItems, isInbound, from])

  return { displayItems, isLoading }
}
